<template>
  <div class="agent-wrap">
    <div class="agent-hdr">
      <div class="agent-hdr-avatar"><i class="ti ti-settings-ai" aria-hidden="true" style="font-size:20px"></i></div>
      <div>
        <div class="agent-hdr-title">AI 어시스턴트</div>
        <div class="agent-hdr-sub"><span class="agent-dot"></span>온라인</div>
      </div>
      
      <button style="margin-left: auto; align-self: flex-start;" @click="hideSidebar">
        <i class="ti ti-layout-sidebar-right-collapse-filled" style="font-size:15px" aria-hidden="true"></i>
      </button>

    </div>
    <div class="msgs" ref="msgEl">
      <div v-for="msg in this.messages" :key="msg.id" class="msgs-row" :class="msg.role">
        <div class="msgs-av" :class="msg.role">
          <i v-if="msg.role==='bot'" class="ti ti-settings-ai" aria-hidden="true" style="font-size:16px"></i>
          <i v-else class="ti ti-user-square-rounded" style="font-size:15px"></i>
        </div>
        <div>
          <div class="msgs-bub" :class="msg.role">
            <div 
              class="copy-container" 
              @mouseenter="hoveredId = msg.id" 
              @mouseleave="hoveredId = null" >
              <transition name="fade">
                <button
                  v-show="hoveredId===msg.id && msg.role==='bot' && msg.type==='answer'"
                  class="copy-btn"
                  @click="copyToClipboard(msg.id, msg.text)"
                >
                  <i v-if="copiedId===null" class="ti ti-copy" />
                  <i v-else class="ti ti-check"/>
                </button>
              </transition>
              <div v-if="msg.type === 'answer'">
                <template v-for="(sec, si) in answerSections(msg.text)">
                  <!-- 그대로 / 회색 한 줄 -->
                  <div v-if="sectionMode(msg.text, sec.kind) === 'show' || sectionMode(msg.text, sec.kind) === 'note'"
                      :key="'sec-' + si" :class="'sec-' + sec.kind">
                    <template v-for="l in sec.lines">
                      <div v-if="l.text.trim()" :key="'line-' + l.line" class="msgs-bub-line"
                          :style="{ marginLeft: (indentOf(l.text) * 8) + 'px' }">
                        <div v-html="convertedHTML(l.text.trim())" />
                        <!-- 기존 차트·표 마크업 그대로 (chartOptions·chartSeries·chartHeight · b-collapse 표) -->
                        <div v-for="item in visualItems(msg.text, l.items)" :key="item.id" class="msgs-bub-visual">
                          <div style="overflow: hidden;" class="msgs-bub-item">
                            <apexchart v-if="item.kind === 'chart'"
                                      :type="item.apex.chart.type" :height="chartProps(item).height"
                                      :options="chartProps(item).options" :series="chartProps(item).series" />
                            <div v-else class="t-panel">
                              <div class="d-flex align-items-center">
                                <div class="q-label">✅ {{ item.title }}</div>
                                <button v-b-toggle="tableDomId(msg, l, item)" class="ml-1 toggle-btn text-center" style="margin-top: 1px; max-height: 16px;">
                                  <span class="when-open" style="color: var(--color-text-tertiary);">▲</span>
                                  <span class="when-closed" style="color: var(--color-text-tertiary);">▼</span>
                                </button>
                              </div>
                              <b-collapse :id="tableDomId(msg, l, item)" :visible="false">
                                <table class="msgs-bub-table">
                                  <thead>
                                    <tr><th v-for="(col, colIdx) in item.columns" :key="'col-' + colIdx">{{ col }}</th></tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(row, r) in item.rows" :key="r">
                                      <td v-for="(cell, c) in row" :key="c" :class="{ num: typeof cell === 'number' }">{{ formatCell(cell) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </b-collapse>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <!-- 되묻기: 다시 물을 질문을 버튼으로 -->
                    <div v-if="sec.kind === 'clarify' && sec.examples && sec.examples.length" class="example-list">
                      <button v-for="q in sec.examples" :key="q" class="example-btn"
                              :disabled="isTyping || isAnswered" @click="askAgain(q)">{{ q }}</button>
                    </div>
                  </div>
                  <!-- 접힌 패널: 분석이 있는 답의 「답하지 못한 부분」 -->
                  <div v-else-if="sectionMode(msg.text, sec.kind) === 'fold'" :key="'sec-fold-' + si" class="sec-fold">
                    <!-- .toggle-btn 은 표의 ▲▼ 용(8px)이다 — 문구가 든 버튼은 따로 -->
                    <button v-b-toggle="'unmet-' + msg.id + '-' + si" class="sec-fold-btn">
                      <span class="chev"></span> 일부 항목은 데이터가 없어 답에서 제외했습니다
                    </button>
                    <b-collapse :id="'unmet-' + msg.id + '-' + si" :visible="false">
                      <template v-for="l in sec.lines">
                        <!-- 첫 줄(머리말 "**답하지 못한 부분**")은 버튼 문구가 대신한다 -->
                        <div v-if="l.text.trim() && l.line !== sec.lines[0].line" :key="'fold-' + l.line"
                            class="msgs-bub-line" :style="{ marginLeft: (indentOf(l.text) * 8) + 'px' }">
                          <div v-html="convertedHTML(l.text.trim())" />
                        </div>
                      </template>
                    </b-collapse>
                  </div>
                </template>
              </div>
              <div v-else>
                <div class="msgs-bub-line" v-for="(line, index) in parseLines(msg.text)" :key="index">
                  <div v-html="convertedHTML(line)" />
                </div>
              </div>
            </div>
          </div>
          <div class="msgs-bub-time">{{ msg.time }}</div>
        </div>
      </div>
      <div v-if="isTyping" class="msgs-row bot">
        <div class="msgs-av bot"><i class="ti ti-settings-ai" aria-hidden="true" style="font-size:15px"></i></div>
        <div class="msgs-typing-wrap">
          <div class="msgs-bub bot">
            <span class="msgs-tdot"></span><span class="msgs-tdot"></span><span class="msgs-tdot"></span>
          </div>
          <div class="msgs-typing-text">{{ typingText }}</div>
        </div>
      </div>
    </div>

    <!-- 질문 선택 패널 -->
    <div class="q-panel">
      <!-- 초기 질문 세트 -->
      <div class="d-flex">
        <div class="q-label">✨ 추천 질문 목록</div>
        <button v-b-toggle.q-collapse class="ml-1 toggle-btn text-center">
          <span class="when-open" style="color: var(--color-text-tertiary);">▲</span>
          <span class="when-closed" style="color: var(--color-text-tertiary);">▼</span>
        </button>
        <button class="reset-btn" style="margin-left: auto;" @click="this.reset">
          <i class="ti ti-refresh" style="font-size:13px" aria-hidden="true"></i>
          초기화
        </button>
      </div>
      <div>
      <b-collapse id="q-collapse" visible>
        <div class="q-list">
          <button
            v-for="q in questions"
            :key="q.id"
            class="q-btn"
            :disabled="isTyping"
            @click="selectQuestion(q)"
          >
            <i class="ti ti-message-question" style="font-size:15px" aria-hidden="true"></i>
            {{ q.label }}
          </button>
        </div>
      </b-collapse>
      </div>
    </div>
    <div class="user-q-panel">
      <agent-question-input :disabled="isAnswered || isTyping" @send="sendUserQuestion" />
    </div>
  </div>
</template>

<script>
import { ref, nextTick } from '@vue/composition-api'

import {
  BFormInput,
  BCollapse,
  BButton,
  VBToggle,
  BInputGroupAppend,
  BInputGroup
} from "bootstrap-vue";

import ToastificationContent from "@core/components/toastification/ToastificationContent";

import AiAgentService from "@/services/cis-aiagent.service";

import axios from "axios"
import moment from "moment";

import useJwt from "@/auth/jwt/useJwt";

import jwtDefaultConfig from "@core/auth/jwt/jwtDefaultConfig"

import ExpXlMarketOverview from './library/ExportXlMarketOverview';
import ExpXlShareofSearch from './library/ExportXlShareofSearch';
import ExpXlSearchTag from './library/ExportXlSearchTag';
import ExpXlInsight from './library/ExportXlInsight';

import makeDtSetMarketOverview from './library/MakeDataSetMarketOverview';
import makeDtSetShareofSearch from './library/MakeDataSetShareofSearch';
import makeDtSetSearchTag from './library/MakeDataSetSearchTag';
import makeDtSetInsight from './library/MakeDataSetInsight';

import { paletteColor, CHART_CHROME } from '@/views/cis-report/utils/palette'

import AgentQuestionInput from './AgentQuestionInput.vue'   // components 에도 등록


const colorMap = ["#3366cc", "#dc3912", "#ff9900", "#109618", "#990099", 
                  "#0099c6", "#dd4477", "#66aa00", "#b82e2e", "#316395", 
                  "#994499", "#22aa99", "#aaaa11", "#6633cc", "#e67300", 
                  "#8b0707", "#651067", "#329262", "#5574a6", "#3b3eac"];

const cancelToken = axios.CancelToken;
var source = cancelToken.source();

const sleep = delay => new Promise(resolve => setTimeout(resolve, delay));

const FAILURE_MESSAGES = {
  network: '서버에 연결하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
  400:     '선택한 분류의 데이터를 찾지 못했습니다. 분류를 확인한 뒤 다시 시도해 주세요.',
  404:     '이전 질문 정보를 찾지 못했습니다. 질문을 처음부터 다시 입력해 주세요.',
  422:     '질문을 처리하지 못했습니다. 질문 내용을 확인한 뒤 다시 입력해 주세요.',
  502:     '분석 서버 연결 설정에 문제가 있어 답변할 수 없습니다. 관리자에게 문의해 주세요.',
  503:     '분석 서버가 준비 중입니다. 잠시 후 다시 시도해 주세요.',
  504:     '분석 서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.',
  dataset: '답변에 필요한 데이터를 준비하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  empty:   '답변을 받지 못했습니다. 잠시 후 다시 시도해 주세요.',
  default: '답변을 만드는 중 문제가 생겼습니다. 잠시 후 다시 시도해 주세요.',
}

export default {
    name: "AiAgentSidebar",
    components: {
      BFormInput,
      BInputGroup,
      BInputGroupAppend,
      BCollapse,
      BButton,
      VBToggle,
      AgentQuestionInput
    },
    directives: {
      "b-toggle": VBToggle,
    },
    data() {
        return {
          user_id : "",
          sess_id : "",

          serv_nm : "",
          sel_question : "",
          sel_category : "",
          typingText : "",

          messages : [],
          questions : [],
          followUps : [],

          isActive : false,
          isAnswered : false,
          isTyping : false,

          msgEl : null,
          cnt : 0,

          // for clipboard
          copiedId: null,
          hoveredId: null
        };
    },
    computed: {
      check_AiAgent_Visibility() {
        return this.$store.getters.getAiAgentVisibility
      },

      check_Selected_Menu() {
        return this.$store.getters.getSelectedCISMenu
      },

      check_Selected_Category() {
        return this.$store.getters.getSelectedCategory
      }
    },
    watch: {
      async check_Selected_Menu(menu) {
        switch(menu) {
          case 'competition':
            this.serv_nm = 'shareofsearch'
            break
          default:
            this.serv_nm = menu
            break
        }

        if (this.isActive)
          await this.updateQuestions()
      },

      async check_Selected_Category(category) {
        this.sel_category = category

        console.log('----- category -----')
        console.log(this.sel_category)
      },

      async check_AiAgent_Visibility(visibility) {
        this.isActive = visibility

        if (this.isActive)
          await this.updateQuestions()
      },

      messages(newMessage) {
        if(newMessage) {
          sessionStorage.removeItem(jwtDefaultConfig.agentMessageKey)

          sessionStorage.setItem(jwtDefaultConfig.agentMessageKey, JSON.stringify(newMessage))
          sessionStorage.setItem(jwtDefaultConfig.agentStatusKey, JSON.stringify({'answered' : this.isAnswered, 'typing': this.isTyping}))
        }
      } 
    },
    methods: {
      async sendUserQuestion(question) {
        await this.selectQuestion({ id: this.questions.length + 1, label: question, question: question, serv_nm: this.serv_nm })
      },

      async pushMessage(role, type, text) {
        this.messages.push({ id: ++this.cnt, role: role, type: type, text: text, time: this.getCurrTime() });
        await this.scrollSync();
      },

      async typeWrite(text) {
        this.messages[this.cnt].text = ""

        if (text == null || text.length == 0) 
          return

        let i = 0;

        const timer = setInterval(() => {
          this.messages[this.cnt].text += text[i++]
          this.scrollSync();

          if (i >= text.length) {
            this.messages[this.cnt].text = text

            clearInterval(timer)

            this.pushMessage('bot', 'request', '질문을 입력하거나 추천 질문 중 하나를 선택해 주세요.')
          }
        }, 20)
      },

      parseLines(text) {
        if (!text) return [];

        const res = text
          .split(/(?=(?<![a-zA-Z가-힣\d])-(?![a-zA-Z가-힣\d])|(?=###))/)
          .map(s => s.trim())
          .filter(s => s);

        return res
      },

      convertedHTML(text) {
        if (!text) return ''

        // 1) 인용 줄인지 먼저 본다 — 이스케이프하면 '>' 가 '&gt;' 가 되어 알아보지 못한다
        var isQuote = /^>\s?/.test(text)
        var s = isQuote ? text.replace(/^>\s?/, '') : text

        // 2) HTML 이스케이프 — 아래에서 만드는 태그 말고는 전부 글자로
        s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

        // 3) 불릿 — 줄 맨 앞일 때만. 기호(•)는 CSS 가 그린다(내어쓰기). 기울임보다 먼저 —
        //    "* 항목 … *강조*" 의 맨 앞 별표를 기울임이 먹지 않게
        var bullet = /^\s*[-*]\s+(.+)$/.exec(s)
        if (bullet) 
          s = bullet[1]

        // 4) 마크다운 → HTML
        s = s
          .replace(/###\s*(.+)/g, '<h6 style="color: #222;"><b>$1</b></h6>')
          .replace(/\*\*(.+?)\*\*/g, '<strong class="md-strong">$1</strong>')   // 밑줄 없이
          .replace(/\*(.+?)\*/g, '<em>$1</em>')
          .replace(/`(.+?)`/g, '<code>$1</code>')
          .replace(/\[\[?(.*?)\]?\]/g, '$1')
        
        if (bullet) 
          s = '<span class="md-li">' + s + '</span>'                  // 내어쓰기

        // 5) 인용 줄이었으면 감싼다 (기존 스타일 그대로)
        if (isQuote)
          s = '<em><blockquote style="background-color: #e8e8e8; border-left: 4px solid #777; padding-left: 8px; color: #777; margin: 10px 0;">' + s + '</blockquote></em>'

        return s
      },

      getCurrTime() {
        return new Date().toLocaleTimeString('ko-KR', { hour:'2-digit', minute:'2-digit' });
      },

      reset() {
        this.messages = [{ id:0, role:'bot', type: 'request', text:'질문을 입력하거나 추천 질문 중 하나를 선택해 주세요.', time: this.getCurrTime() }];
        this.isAnswered = false;
        this.cnt = 0;
      },

      async scrollSync() {
        await nextTick(); 
        
        if(this.$refs.msgEl) 
          this.$refs.msgEl.scrollTop = this.$refs.msgEl.scrollHeight;
      },

      async hideSidebar() {
        this.$store.dispatch('callAiAgentVisibility', { visibility : false })
      },

      calcSelectPeriod(periodType) {
        var en_dt = moment().add(-2, 'days').toDate(); // 데이터 업데이트 시점을 고려
        var st_dt = moment(en_dt).add(-(Number(periodType) * 12 - 1) , 'month').toDate();

        return { 'st_dt': moment(st_dt).format("YYYY-MM"), 'en_dt': moment(en_dt).format("YYYY-MM") }
      },

      async selectQuestion(q) {
        try {
          if(this.isTyping) 
            return;
          
          this.pushMessage('user', 'question', q.label)

          this.isTyping = true;
          this.isAnswered = true;
          this.sel_question = q.question;

          await this.scrollSync();

          var dt_path = ""

          this.typingText = '데이터 셋 검토 중...'

          // for Debug
          await this.makeBaseDataset()

          this.typingText = '사용자 질문에 대한 답변 생성 중...'

          const resp = await AiAgentService.callAiAgent_Request(
          {
            "session_id" : this.sess_id,
            "user_id": this.user_id, 
            // "user_id": "smoke", // for test
            "question": this.sel_question,
            "cat_id": this.sel_category
          }, source)

          console.log('----- AI Agent | Response -----')
          console.log(resp)

          if (resp && resp.status == 200 && resp.data != null) {
            this.typingText = '참조 데이터 셋 생성 중...'

            var resp_dt = resp.data.data != null ? resp.data.data : resp.data;

            switch(resp_dt.status) {
              case "needs_dataset":
                const plan_id = resp_dt.plan_id;
                const dt_request = resp_dt.dataset_request;
                const families = dt_request.families;

                for (const family of dt_request.to_build) {
                  var serv_nm = "";
                  var need_dt_st = "";
                  var need_dt_en = "";

                  switch(family) {
                    case "market_overview":
                      serv_nm = "marketoverview"
                      need_dt_st = families.market_overview.start;
                      need_dt_en = families.market_overview.end;
                      break;
                    case "share_of_search":
                      serv_nm = "shareofsearch"
                      need_dt_st = families.share_of_search.start;
                      need_dt_en = families.share_of_search.end;
                      break;
                    case "search_tag":
                      serv_nm = "searchtag"
                      need_dt_st = families.search_tag.start;
                      need_dt_en = families.search_tag.end;
                      break;
                    case "insight":
                      serv_nm = "insight"
                      need_dt_st = families.insight.start;
                      need_dt_en = families.insight.end;
                      break;
                  }

                  const dataset = await this.getDataset(serv_nm, need_dt_st, need_dt_en)

                  dt_path = await this.saveToExcel(this.sel_category, serv_nm, need_dt_st, need_dt_en, dataset, dt_request.target_dir)

                  if (dt_path.length == 0)
                    // throw new Error("데이터 셋 생성 실패!!")
                    throw this.agentFailure(null, 'dataset')
                }

                this.typingText = '참조 데이터 셋 생성 완료...'

                await sleep(1);

                this.typingText = '사용자 질문에 대한 답변 생성 중...'

                const resp_fn = await AiAgentService.callAiAgent_Request(
                {
                  "session_id" : this.sess_id,
                  "user_id": this.user_id, 
                  // "user_id": "smoke", // for test
                  "question": this.sel_question,
                  "cat_id": this.sel_category,
                  "plan_id": plan_id
                }, source)

                console.log('----- AI Agent | Response (2nd) -----')
                console.log(resp_fn)

                if (resp_fn && resp_fn.status == 200 && resp_fn.data != null) {
                  setTimeout(async () => {
                    this.isTyping = false;
                    this.isAnswered = false;

                    var resp_fn_dt = resp_fn.data.data != null ? resp_fn.data.data : resp_fn.data;

                    const ans_data = {
                      sections:    resp_fn_dt.sections,      // 무엇을 보여 줄지 고르는 단위
                      display:     resp_fn_dt.display,       // sections 가 없는 옛 답(이력)용
                      display_map: resp_fn_dt.display_map,
                      visuals:     resp_fn_dt.visuals,
                      answer_mode: resp_fn_dt.answer_mode,
                      status:      resp_fn_dt.status,
                      req_sq:      resp_fn_dt.req_sq,
                    }

                    this.pushMessage('bot', 'answer', Object.freeze(ans_data))   // 두 응답 경로 모두

                    // await this.$nextTick();
                    // await this.typeWrite(resp_fn_dt.display)
                    // await this.typeWrite(response.data.response_text) // for local test
                  }, 500);
                }
                else
                  // throw new Error("데이터 셋 분석 또는 결과 정리 실패!!")
                  throw this.agentFailure(resp_fn)

                break;
              case "answered":
                this.typingText = '사용자 질문에 대한 답변 생성 완료...'

                setTimeout(async () => {
                  this.isTyping = false;
                  this.isAnswered = false;

                  const ans_data = {
                    sections:    resp_dt.sections,      // 무엇을 보여 줄지 고르는 단위
                    display:     resp_dt.display,       // sections 가 없는 옛 답(이력)용
                    display_map: resp_dt.display_map,
                    visuals:     resp_dt.visuals,
                    answer_mode: resp_dt.answer_mode,
                    status:      resp_dt.status,
                    req_sq:      resp_dt.req_sq,
                  }

                  this.pushMessage('bot', 'answer', Object.freeze(ans_data))   // 두 응답 경로 모두

                  // await this.$nextTick();
                  // await this.typeWrite(resp_dt.display)
                  // await this.typeWrite(response.data.response_text) // for local test
                }, 500);

                break;
            }
          }
          else
            // throw new Error("데이터 셋 분석 또는 결과 정리 실패!!")
            throw this.agentFailure(resp)
        }
        catch (err) {
          // console.log(err)

          // setTimeout(async () => {
          //   this.isTyping = false;
          //   this.isAnswered = false;
          //   this.pushMessage('bot', 'request', '잠시 후 다시 시도해 주세요.')
          // }, 500);

          console.error('[AI Agent] 실패 —', (err && err.detail) || err)   // 원인은 개발자 콘솔에만

          var message = (err && err.userMessage) || FAILURE_MESSAGES.default
          
          setTimeout(async () => {
            this.isTyping = false;
            this.isAnswered = false;
            this.pushMessage('bot', 'request', message)
          }, 500);
        }       
      },

      async updateQuestions() {
        this.questions = []

        if (this.serv_nm.length == 0)
        {
          this.serv_nm = this.$store.getters.getSelectedCISMenu

          if (this.serv_nm == 'competition')
            this.serv_nm = 'shareofsearch'
        }
         
        const response = await AiAgentService.getServiceQuestion({'serv_nm' : this.serv_nm}, source)

        if(response && response.status == 200) {
          var id = 1

          for (const q of response.data.data) {
            this.questions.push({'id': id++, 'label': q.qrepStr, 'question': q.qstr, 'serv_nm': this.serv_nm})
          }
        }

        await this.scrollSync();

        this.updateFollowUps();
      },

      updateFollowUps() {
        this.followUps = []

        switch(this.serv_nm) {
          case 'marketoverview':
            this.followUps.push(
              {'id': 0, 'label': '최근 3년'},
              {'id': 1, 'label': '최근 5년'},
              {'id': 2, 'label': '최근 7년'},
              {'id': 3, 'label': '👈 다시 질문 선택하기'},
            )

            break
          case 'shareofsearch':
          case 'searchtag':
          case 'insight':
            this.followUps.push(
              {'id': 0, 'label': '최근 1년'},
              {'id': 1, 'label': '최근 2년'},
              {'id': 2, 'label': '최근 3년'},
              {'id': 3, 'label': '👈 다시 질문 선택하기'},
            )
            
            break
        }
      },

      async getDataset(serv_nm, st_dt, en_dt) {
        switch(serv_nm) {
          case 'marketoverview':
            const useMakeDtsetMarketOverview = new makeDtSetMarketOverview(this.sel_category, st_dt, en_dt)

            await useMakeDtsetMarketOverview.initialize()

            return await useMakeDtsetMarketOverview.run()

          case 'shareofsearch':
            const useMakeDtShareofSearch = new makeDtSetShareofSearch(this.sel_category, st_dt, en_dt)

            await useMakeDtShareofSearch.initialize()

            return await useMakeDtShareofSearch.run()

          case 'searchtag':
            const useMakeDtSearchTag = new makeDtSetSearchTag(this.sel_category, st_dt, en_dt)

            await useMakeDtSearchTag.initialize()

            return await useMakeDtSearchTag.run()
            
          case 'insight':
            const useMakeDtInsight = new makeDtSetInsight(this.sel_category, st_dt, en_dt)

            await useMakeDtInsight.initialize()

            return await useMakeDtInsight.run()
        }
      },

      async getMajorList(serv_nm, st_dt, en_dt) {
        switch(serv_nm) {
          case 'marketoverview':
            const useMakeDtsetMarketOverview = new makeDtSetMarketOverview(this.sel_category, st_dt, en_dt)

            await useMakeDtsetMarketOverview.initialize()

            return useMakeDtsetMarketOverview.majorClsList

          case 'shareofsearch':
            const useMakeDtShareofSearch = new makeDtSetShareofSearch(this.sel_category, st_dt, en_dt)

            await useMakeDtShareofSearch.initialize()

            var res = []

            for (const major of useMakeDtShareofSearch.majorClsList) {
              res.push(major.title)
            }

          case 'searchtag':
            const useMakeDtSearchTag = new makeDtSetSearchTag(this.sel_category, st_dt, en_dt)

            await useMakeDtSearchTag.initialize()

            var res = []

            for (const major of useMakeDtSearchTag.majorClsList) {
              res.push(major.title)
            }
            
          case 'insight':
            const useMakeDtInsight = new makeDtSetInsight(this.sel_category, st_dt, en_dt)

            await useMakeDtInsight.initialize()

            var res = []

            for (const major of useMakeDtInsight.majorClsList) {
              res.push(major.title)
            }

            return res
        }
      },

      async saveToExcel(cat_id, serv_nm, st_dt, en_dt, dataset, target_dir = "") {
        switch(serv_nm) {
          case 'marketoverview':
            const expXlMarketOverviewCls = new ExpXlMarketOverview()

            return await expXlMarketOverviewCls.xlExport(cat_id, st_dt, en_dt, dataset, target_dir)

          case 'shareofsearch':
            const expXlShareofSearch = new ExpXlShareofSearch()

            return await expXlShareofSearch.xlExport(cat_id, st_dt, en_dt, dataset, target_dir)

          case 'searchtag':
            const expXlSearchTag = new ExpXlSearchTag()

            return await expXlSearchTag.xlExport(cat_id, st_dt, en_dt, dataset, target_dir)

          case 'insight':
            const expXlInsight = new ExpXlInsight()

            return await expXlInsight.xlExport(cat_id, st_dt, en_dt, dataset, target_dir)   
        }
      },

      async copyToClipboard(msgId, answered) {
        try {
          if (answered && answered.display.length == 0)
            throw new Error("AI 분석 결과 없음")

          await navigator.clipboard.writeText(answered.display)

          this.copiedId = msgId
          setTimeout(() => (this.copiedId = null), 2000)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: `Copy to clipboard successful`,
              icon: "EditIcon",
              variant: "success",
            },
          });
        } catch (err) {
          console.error('클립보드 복사 실패:', err)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: `Copy to clipboard failed`,
              icon: "EditIcon",
              variant: "success",
            },
          });
        }
      },

      async fileExist(cat_id, serv_nm, st_dt, en_dt, target_dir = "") {
        switch(serv_nm) {
          case 'marketoverview':
            const expXlMarketOverviewCls = new ExpXlMarketOverview()

            return await expXlMarketOverviewCls.fileExist(cat_id, st_dt, en_dt, target_dir);

          case 'searchtag':
            const expXlSearchTag = new ExpXlSearchTag()

            return await expXlSearchTag.fileExist(cat_id, st_dt, en_dt, target_dir);

          case 'insight':
            const expXlInsight = new ExpXlInsight()

            return await expXlInsight.fileExist(cat_id, st_dt, en_dt, target_dir);
        }
      },

     async makeBaseDataset() {
        this.typingText = '기본 데이터 셋 점검...'

        var en_dt = moment().add(-1, 'month').toDate(); // 데이터 업데이트 시점을 고려
        var st1_dt = moment(en_dt).add(-(2 * 12 - 1) , 'month').toDate();
        var st2_dt = moment(en_dt).add(-(3 * 12 - 1) , 'month').toDate();

        // 1. 순회할 서비스 설정들을 배열로 깔끔하게 정리합니다.
        const services = [
          { serv_nm: "marketoverview", need_dt_st: moment(st2_dt).format("YYYY-MM"), need_dt_en: moment(en_dt).format("YYYY-MM") },
          { serv_nm: "searchtag",      need_dt_st: moment(st1_dt).format("YYYY-MM"), need_dt_en: moment(en_dt).format("YYYY-MM") },
          { serv_nm: "insight",        need_dt_st: moment(st1_dt).format("YYYY-MM"), need_dt_en: moment(en_dt).format("YYYY-MM") }
        ];

        // 2. 반복문을 통해 안전하게 비동기 처리를 수행합니다.
        for (const service of services) {
          const { serv_nm, need_dt_st, need_dt_en } = service;
          const res_path = `_by_period/${this.sel_category}/gen_${need_dt_en}/${need_dt_st}_${need_dt_en}`;

          // 파일 존재 여부 확인하기
          const res = await this.fileExist(this.sel_category, serv_nm, need_dt_st, need_dt_en, res_path)

          if (res)
            continue;

          this.typingText = '기본 데이터 셋 생성 중...'

          // 데이터셋 가져오기
          const dataset = await this.getDataset(serv_nm, need_dt_st, need_dt_en);
          
          // 엑셀 저장 (각 루프마다 독립된 const dt_path 변수가 생성되어 안전합니다)
          const dt_path = await this.saveToExcel(this.sel_category, serv_nm, need_dt_st, need_dt_en, dataset, res_path);

          // 안전하게 조건 검사 (옵셔널 체이닝 포함)
          if (dt_path.length === 0) {
            throw new Error(`${serv_nm} 데이터 셋 생성 실패!!`);
          }
        }

        this.typingText = '기본 데이터 셋 점검 완료...'

        await sleep(1);
      },

      // vue-apexcharts 는 series 를 prop 으로 따로 받는다 — apex 에서 떼어 낸다.
      chartSeries(item) {
        if (item.apex.chart.type === 'scatter') {
          // 그룹별로 데이터를 묶기 위해 맵(Map)을 활용하는 방식
          var seriesMap = {};

          for (const sub_series of item.apex.series) {           
            for (const data of sub_series.data) {
              // 기존 그룹이 없다면 새로 생성
              if (!seriesMap[data.name]) {
                seriesMap[data.name] = [];
              }

              seriesMap[data.name].push({
                x: data.x,
                y: data.y,
                name: sub_series.name
              });
            }
          }

          // 맵에 정렬된 데이터를 ApexCharts 포맷으로 변환
          var tmp_series = Object.keys(seriesMap).map(key => {
            return {
              name: key,
              data: seriesMap[key]
            };
          });

          return tmp_series;
        }
        // else if(item.apex.chart.type === 'line') {
        //   // for (const sub_series of item.apex.series) { 
        //   //   sub_series.hidden
        //   // }
        // }
        else 
          return item.apex.series
      },
      calcCategoryCount(opt) {
        if (!opt) return 0;

        const typ = opt.chart && opt.chart.type;

        // 1. 원형 차트 계열 (Pie, Donut 등) 처리
        if (['pie', 'donut', 'radialBar', 'polarArea'].includes(typ)) {
          return opt.labels ? opt.labels.length : 0;
        }

        // 2. 가로형 바 차트(Horizontal Bar) 여부 체크 🌟
        const isHorizontal = opt.plotOptions && 
                            opt.plotOptions.bar && 
                            opt.plotOptions.bar.horizontal === true;

        // 3. [조건 추가] 가로형 바 차트인 경우 🌟
        // 가로형은 series[0].data의 개수가 곧 행(Row)의 개수(카테고리 수)가 됩니다.
        if (isHorizontal) {
          if (opt.series && opt.series[0] && opt.series[0].data) {
            // return opt.series[0].data.length;
            return 0;
          }
          // 만약 세로축 카테고리 배열이 yaxis 등에 명시되어 있다면 그것을 반환
          if (opt.yaxis && opt.yaxis.categories) {
            return opt.yaxis.categories.length;
          }
        }

        // 4. 일반 세로형 차트 (Line, Column 등) 처리
        // 명시된 categories가 있다면 해당 배열의 길이를 우선 신뢰합니다.
        if (opt.xaxis && opt.xaxis.categories) {
          return opt.xaxis.categories.length;
        }

        // 5. 일반 차트 중 categories 배열이 없고 series 데이터만 존재할 때 (XY 좌표형 데이터 등)
        if (opt.series && opt.series[0] && opt.series[0].data) {
          return opt.series[0].data.length;
        }

        return 0;
      },
      chartOptions(item) {
        const { series, ...options } = item.apex
        // const colors = series.map((s) => paletteColor(this.getColor(s.name), false))
        const chrome = CHART_CHROME.light

        const cat_cnt = this.calcCategoryCount(item.apex)

        // 시계열은 categories 가 빈 달 없이 온다 (값이 없는 달은 null). null 을 선으로 잇지 않게 둔다.
        return {
          ...options,
          chart: { 
            ...options.chart, 
            toolbar: { show: false }, 
            animations: { speed: 250 },
            fontFamily: 'NanumBarunGothic !important',
            foreColor: chrome.textSecondary,
            background: 'transparent',
            zoom: { enabled: false },
            parentHeightOffset: 0,
          },
          title: { ...options.title, margin: 0, offsetY: 10, style: {fontSize: '12px'} },
          colors: colorMap,
          grid: { padding: {top: 0, bottom: 0}, borderColor: 'rgba(255, 255, 255, 0.08)'},
          dataLabels: { enabled: options.chart.type === 'bar' && (options.series || []).length === 1 },
          noData: { text: '데이터 없음' },
          xaxis: {
            ...options.xaxis,
            labels: {
              show: true,
              style: { colors: chrome.muted, fontSize: '9px' },
              rotate: -45,
              hideOverlappingLabels: false,
              formatter: this.formatValue,
            },
            title: {
              text: options.xaxis.title != undefined ? options.xaxis.title.text : undefined,
              style: { fontSize: '10px', fontWeight: '900 !important' }
            },
            axisBorder: { color: chrome.axis },
            axisTicks: { color: chrome.axis },
            tooltip: { enabled: false },
            tickAmount: cat_cnt < 5 ? 5 : cat_cnt > 20 ? 20 : cat_cnt,
          },
          yaxis: {
            ...options.yaxis,
            labels: {
              show: true,
              style: { colors: chrome.muted, fontSize: '9px' },
              formatter: this.formatValue,
            },
            title: {
              // show: true,
              text: options.chart.type === 'scatter' ? options.yaxis.title.text : undefined,
              style: { fontSize: '10px', fontWeight: '900 !important' },
            }
          },
          tooltip: {
            x: { show: true },
            y: { formatter: this.formatValue },
            followCursor: true,
            shared: false,
            intersect: true,
            enabled: true,
            fixed: { enabled: false },
            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
              const seriesIdx = seriesIndex
              const dataPtIdx = dataPointIndex

              var hexColor = w.globals.colors[seriesIdx].replace('#', '')
              const r = parseInt(hexColor.substring(0, 2), 16)
              const g = parseInt(hexColor.substring(2, 4), 16)
              const b = parseInt(hexColor.substring(4, 6), 16)
              hexColor = `rgb(${r}, ${g}, ${b})`

              var val = `${this.tooltipFormatValue(Number(series[seriesIdx][dataPtIdx]))}`

              if (w.config.chart.type === 'scatter') {
                return '<div>' +
                  '<div class="apexcharts-tooltip-title" style="font-family: NanumBarunGothic !important; font-size: 10px;">' + w.globals.seriesNames[seriesIdx] + '</div>' +
                  '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">' +
                  '<span class="apexcharts-tooltip-marker" style="background-color: ' + hexColor + ';"></span>' +
                  '<div class="apexcharts-tooltip-text" style="font-family: NanumBarunGothic !important; font-size: 10px;">' +
                  '<div class="apexcharts-tooltip-y-group">' +
                  '<span class="apexcharts-tooltip-text-label">' + val + ' ( ' + w.globals.initialSeries[seriesIdx].data[dataPointIndex].name + ' )' + '</span>' +
                  '</div>' +
                  '<div class="apexcharts-tooltip-z-group">' +
                  '<span class="apexcharts-tooltip-text-z-label"></span>' +
                  '<span class="apexcharts-tooltip-text-z-value"></span>' +
                  '</div></div></div></div>'
              }
              else if (w.config.chart.type === 'line') {
                return '<div>' +
                  '<div class="apexcharts-tooltip-title" style="font-family: NanumBarunGothic !important; font-size: 10px;">' + w.globals.seriesNames[seriesIdx] + '</div>' +
                  '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">' +
                  '<span class="apexcharts-tooltip-marker" style="background-color: ' + hexColor + ';"></span>' +
                  '<div class="apexcharts-tooltip-text" style="font-family: NanumBarunGothic !important; font-size: 10px;">' +
                  '<div class="apexcharts-tooltip-y-group">' +
                  '<span class="apexcharts-tooltip-text-label">' + w.globals.categoryLabels[dataPointIndex] + ' : ' + '</span>' +
                  '<span class="apexcharts-tooltip-text-value">' + val + '</span>' +
                  '</div>' +
                  '<div class="apexcharts-tooltip-z-group">' +
                  '<span class="apexcharts-tooltip-text-z-label"></span>' +
                  '<span class="apexcharts-tooltip-text-z-value"></span>' +
                  '</div></div></div></div>'
              }
              else
                return '<div>' +
                  '<div class="apexcharts-tooltip-title" style="font-family: NanumBarunGothic !important; font-size: 10px;">' + w.globals.labels[dataPtIdx] + '</div>' +
                  '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">' +
                  '<span class="apexcharts-tooltip-marker" style="background-color: ' + hexColor + ';"></span>' +
                  '<div class="apexcharts-tooltip-text" style="font-family: NanumBarunGothic !important; font-size: 10px;">' +
                  '<div class="apexcharts-tooltip-y-group">' +
                  '<span class="apexcharts-tooltip-text-label">' + w.globals.seriesNames[seriesIdx] + ' : ' + '</span>' +
                  '<span class="apexcharts-tooltip-text-value">' + val + '</span>' +
                  '</div>' +
                  '<div class="apexcharts-tooltip-z-group">' +
                  '<span class="apexcharts-tooltip-text-z-label"></span>' +
                  '<span class="apexcharts-tooltip-text-z-value"></span>' +
                  '</div></div></div></div>'
            },
          },
          legend: {
            ...options.legend,
            show: true,
            position: "top",
            horizontalAlign: "center",
            itemMargin: {
              horizontal: 5,
              vertical: 5,
            },
            markers: {
              size: 4,           // marker radius in pixels
              shape: 'circle',   // 'circle' | 'square' | 'star' | 'plus' | 'cross'
              strokeWidth: 1,
              strokeColors: '#fff',
              fillOpacity: 0.9,
            },
            onItemClick: {
              toggleDataSeries: true
            },
            onItemHover: {
              highlightDataSeries: true
            },
          },
          markers: {
            ...options.markers,
            size: 3,           // marker radius in pixels
            shape: 'circle',   // 'circle' | 'square' | 'star' | 'plus' | 'cross'
            strokeWidth: 1,
            strokeColors: '#fff',
            fillOpacity: 0.9,
            hover: {
              sizeOffset: 2    // extra radius on hover, giving the user visual feedback
            }
          }
        }
      },
      chartHeight(item) {
        const bar = item.apex.plotOptions && item.apex.plotOptions.bar
        const n = (item.apex.xaxis && item.apex.xaxis.categories || []).length
        return bar && bar.horizontal ? Math.max(240, 24 * n + 80) : 300
      },
      formatCell(cell) {
        if (cell === null || cell === undefined) return '—'
        if (typeof cell === 'number') return Number.isInteger(cell) ? cell.toLocaleString() : cell.toLocaleString(undefined, { maximumFractionDigits: 2 })
        return cell
      },
      formatValue(value) {
        if (value === null || value === undefined) return '-'

        if (!this.isNumber(value))
          return value;

        let val = Math.round(value * 10) / 10;

        if (val >= 10 ** 3 && val < 10 ** 6)
          return (val / 1000).toFixed(1) + 'K';
        else if (val >= 10 ** 6)
          return (val / 1000000).toFixed(1) + 'M';
        // else if (this.isPercentLike)
        //   return (val).toLocaleString() + '%';
        else
          return (val).toFixed(1).toLocaleString();
      },
      tooltipFormatValue(value) {
        if (value === null || value === undefined) return '-'

        let val = Math.round(value * 10) / 10;

        // if (this.isPercentLike)
        //   return (val).toLocaleString() + '%';
        // else
        return Number(val.toFixed(1)).toLocaleString();
      },
      getColor() {
        const assigned = new Map()
        let next = 0
        return function getColor(seriesName) {
          if (!assigned.has(seriesName)) {
            assigned.set(seriesName, next % CATEGORICAL_PALETTE.length)
            next += 1
          }
          return assigned.get(seriesName)
        }
      },
      isNumber(value) {
        // 1. 이미 숫자 타입인 경우 (NaN이 아닌 경우만 true)
        if (typeof value === "number") {
            return !isNaN(value);
        }

        // 2. null, undefined, 불리언(true/false) 등 숫자 변환 시 왜곡되는 타입 방지
        if (value === null || value === undefined || typeof value === "boolean") {
            return false;
        }
        
        // 3. 문자열일 경우, 공백만 있는 문자열 검사 후 Number 변환
        if (typeof value === "string" && value.trim() === "") {
            return false;
        }
        
        // 4. 최종적으로 Number로 바꿨을 때 NaN이 아니면 숫자형태입니다.
        return !isNaN(Number(value));
      },
      // 조각 목록 — sections 가 없는 옛 답은 display 전체를 한 조각으로
      answerSections(ans) {
        if (ans.sections && ans.sections.length) 
          return ans.sections

        var kind = ans.answer_mode === 'clarify' ? 'clarify' : ans.answer_mode === 'out_of_scope' ? 'unmet' : 'analysis'

        return [{ kind: kind, lines: this.toLines(ans) }]
      },
      // 표시 정책 — 무엇을 어떻게 보일지 여기 한 곳에서 정한다
      //   'show' 그대로 · 'fold' 접힌 패널 · 'note' 회색 한 줄 · 'hide' 숨김
      sectionMode(ans, kind) {
        var hasAnalysis = this.answerSections(ans).some(function (s) { return s.kind === 'analysis' })
        if (kind === 'analysis' || kind === 'clarify') return 'show'
        if (kind === 'unmet') return hasAnalysis ? 'fold' : 'show'   // 분석이 없으면 사유가 곧 답이다
        if (kind === 'notice') return 'note'
        if (kind === 'preamble') return 'note'
        return 'hide'                                                // preamble · dataset
      },
      // display_map 모양의 줄 목록 (sections 가 없을 때만 쓴다) — 위 toLines 와 같은 메서드다
      toLines(ans) {
        var byLine = {}
        ;(ans.display_map || []).forEach(function (m) { byLine[m.line] = m })
        return (ans.display || '').split('\n').map(function (text, line) {
          var m = byLine[line] || {}
          return { line: line, text: text, steps: m.steps || [], items: m.items || [] }
        })
      },
      indentOf(text) {
        return text.length - text.replace(/^ +/, '').length            // "  - …" 이면 2
      },
      // 표 패널(b-collapse)의 DOM id — **메시지 · 줄 · 항목** 셋으로 만든다.
      // item.id 는 그 답 안에서만 고유하다(같은 종류의 질문이면 다른 답에서도 "s2-brand_share" 가 나온다).
      // req_sq 는 쓰지 않는다 — 응답에서 빠지거나(저장 실패·옛 이력) 잘못 읽으면 모든 메시지가 같은 id 가 된다.
      tableDomId(msg, line, item) {
        return 'tbl-' + msg.id + '-' + line.line + '-' + item.id
      },
      // 되묻기 버튼 — 그 질문을 그대로 다시 보낸다 (입력창의 sendUserQuestion 과 같은 길)
      askAgain(q) {
        this.selectQuestion({ id: this.questions.length + 1, label: q, question: q, serv_nm: this.serv_nm })
      },
      // id 목록 → visuals 의 표·차트 항목 (id 순서대로)
      visualItems(ans, ids) {
        var byId = {}
        ;(ans.visuals || []).forEach(function (v) {
          (v.items || []).forEach(function (it) { byId[it.id] = it })
        })
        return (ids || []).map(function (id) { return byId[id] }).filter(Boolean)
      },

      chartProps(item) {
        var cached = this._chartCache.get(item)

        if (!cached) {
          cached = { options: this.chartOptions(item), series: this.chartSeries(item), height: this.chartHeight(item) }
          this._chartCache.set(item, cached)
        }

        return cached
      },

      agentFailure(resp, kind) {
        var key = kind || (!resp ? 'network' : (resp.status === 200 ? 'empty' : String(resp.status)))
        var err = new Error('AI Agent 호출 실패: ' + key)
        err.userMessage = FAILURE_MESSAGES[key] || FAILURE_MESSAGES.default
        err.detail = resp ? { status: resp.status, data: resp.data } : null
        return err
      },
    },

    setup() {
      console.log('----- setup -----')
      return {}
    },

    async created() {
      console.log('----- created -----')

      this.user_id = String(useJwt.getUserData().adacctId)
      this.sess_id = crypto.randomUUID()
    
      this.messages = []
      this.messages.push(
        { id: 0, role:'bot', type: 'request', text:'질문을 입력하거나 추천 질문 중 하나를 선택해 주세요.', time: this.getCurrTime() } 
      )

      this.followUps = []
      this.questions = []

      this._chartCache = new WeakMap() /* 기존 created 내용에 한 줄 추가 */
    },

    mounted() {
      const msgData = JSON.parse(sessionStorage.getItem(jwtDefaultConfig.agentMessageKey) ?? '[]')
      const statData = JSON.parse(sessionStorage.getItem(jwtDefaultConfig.agentStatusKey))

      if (statData) {
        this.isAnswered = statData.answered
        this.isTyping = statData.typing
      }

      // mounted() 에서 sessionStorage 로 대화를 되살릴 때도
      msgData.forEach(function (m) { 
        if (m.type === 'answer' && m.text) 
          m.text = Object.freeze(m.text) 
      })

      if (msgData.length > 0) {
        this.messages = msgData
        this.cnt = msgData.length - 1
      }
    }
}
</script>

<style lang="scss">
@import "@core/scss/base/bootstrap-extended/_variables.scss";
@import "@core/scss/vue/libs/chart-apex.scss";

/* 화살표(→ 등)만 Malgun Gothic 으로 — NanumBarunGothic 의 →는 11~12px 에서 높이 3px 의 가는 선이라
   줄표(—)처럼 보인다(측정: 높이 3px → 5px). unicode-range 밖의 글자(한글·숫자)는 이 face 를 쓰지 않는다.
   본문·표·LLM 이 쓴 문장 어디서 와도 한 번에 고쳐진다. */
@font-face {
  font-family: 'AgentArrow';
  src: local('Malgun Gothic'), local('맑은 고딕'), local('Apple SD Gothic Neo'), local('Arial');
  unicode-range: U+2190-2199;
}

/* 키프레임 이름은 전역이다 — 다른 곳과 겹치지 않게 이름에 앞을 붙였다 */
@keyframes agent-typing-blink {
  0%, 80%, 100% { opacity: 0.2; }
  40%           { opacity: 1; }
}

.agent-wrap {
  /* 색·모서리 변수 — :root 에서 옮겼다. 이 아래에서만 쓴다 */
  --border-radius-lg: 12px;
  --border-radius-md: 8px;

  --color-background-primary: rgba(255, 255, 255, 1);
  --color-background-secondary: rgba(245, 244, 237, 1);
  --color-background-info: rgba(214, 228, 246, 1);

  --color-border-primary: rgba(31, 30, 29, 0.4);
  --color-border-secondary: rgba(31, 30, 29, 0.3);
  --color-border-tertiary: rgba(31, 30, 29, 0.15);

  --color-text-primary: rgba(20, 20, 19, 1);
  --color-text-secondary: rgba(61, 61, 58, 1);
  --color-text-tertiary: rgba(115, 114, 108, 1);
  --color-text-info: rgba(50, 102, 173, 1);
  --color-text-success: rgba(38, 91, 25, 1);

  // 어두운 테마 — 쓰게 되면 위 값을 이것으로
  // --color-background-primary: rgba(48, 48, 46, 1);   --color-background-secondary: rgba(38, 38, 36, 1);
  // --color-background-info: rgba(37, 62, 95, 1);
  // --color-border-primary: rgba(222, 220, 209, 0.4);  --color-border-secondary: rgba(222, 220, 209, 0.3);
  // --color-border-tertiary: rgba(222, 220, 209, 0.15);
  // --color-text-primary: rgba(250, 249, 245, 1);      --color-text-secondary: rgba(194, 192, 182, 1);
  // --color-text-tertiary: rgba(156, 154, 146, 1);     --color-text-info: rgba(128, 170, 221, 1);
  // --color-text-success: rgba(122, 185, 72, 1);

  font-family: 'AgentArrow', 'NanumBarunGothic', sans-serif;   /* 화살표만 AgentArrow — 위 @font-face */
  display: flex;
  flex-direction: column;
  height: calc(100% - 20px);
  border: 3px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-right: 5px;
  background: var(--color-background-primary);

  /* ── 머리 ─────────────────────────────────────────── */
  .agent-hdr {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 0.5px solid var(--color-border-tertiary);
    background: var(--color-background-secondary);
  }
  .agent-hdr-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--color-background-info);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-info);
    font-size: 15px;
  }
  .agent-hdr-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }
  .agent-hdr-sub {
    font-size: 11px;
    color: var(--color-text-success);
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .agent-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #3a9;
    display: inline-block;
  }

  /* ── 메시지 ───────────────────────────────────────── */
  .msgs {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .msgs-row {
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }
  .msgs-row.user {
    flex-direction: row-reverse;
  }
  .msgs-av {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 500;
    flex-shrink: 0;
  }
  .msgs-av.user {
    background: var(--color-background-info);
    color: var(--color-text-info);
  }
  .msgs-av.bot {
    background: var(--color-background-secondary);
    color: var(--color-text-secondary);
    border: 0.5px solid var(--color-border-tertiary);
  }
  .msgs-bub {
    max-width: 100%;
    padding: 3px 5px;
    border-radius: 14px;
    font-size: 12px;
    line-height: 1.6;
    color: var(--color-text-secondary);
  }
  .msgs-bub-line {
    margin: 4px 0 4px 0;  /* 항목 사이 간격 */
    padding-left: 4px;
    line-height: 1.8;     /* 줄 간격 */
  }
  .msgs-bub-line:last-child {
    margin-bottom: 0;     /* 마지막 항목 하단 여백 제거 */
  }
  .msgs-bub.bot {
    width: fit-content;
    min-width: unset;
    background: var(--color-background-secondary);
    border-bottom-left-radius: 3px;
    border: 0.5px solid var(--color-border-tertiary);
  }
  .msgs-bub.user {
    background: var(--color-background-info);
    color: var(--color-text-info);
    border-bottom-right-radius: 3px;
  }
  .msgs-bub-time {
    font-size: 10px;
    color: var(--color-text-tertiary);
    margin-top: 3px;
  }
  .msgs-row.user .msgs-bub-time {
    text-align: right;
  }

  /* 본문 마크다운 (convertedHTML 이 만드는 클래스) */
  .msgs-bub .md-strong { font-weight: 700; color: var(--color-text-primary); }
  .msgs-bub .md-li { display: block; position: relative; padding-left: 12px; }
  .msgs-bub .md-li::before { content: "•"; position: absolute; left: 2px; color: var(--color-text-tertiary); }

  /* 입력 중 */
  .msgs-typing-wrap {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .msgs-typing-text {
    font-size: 11px;
    color: var(--color-text-info);
    white-space: nowrap;
  }
  .msgs-tdot {
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--color-text-tertiary);
    animation: agent-typing-blink 1.2s infinite;
    margin: 0 2px;
  }
  .msgs-tdot:nth-child(2) { animation-delay: 0.2s; }
  .msgs-tdot:nth-child(3) { animation-delay: 0.4s; }

  /* ── 표·차트 ──────────────────────────────────────── */
  /* 표·차트 하나하나를 카드로 — 차트 바로 밑에 표가 붙어도 경계가 보이게 */
  .msgs-bub-visual {
    margin: 8px 0;
    border: 0.5px solid var(--color-border-tertiary);
    border-radius: var(--border-radius-md);
    background: var(--color-background-primary);
    overflow: hidden;
  }
  .msgs-bub-visual + .msgs-bub-visual { margin-top: 6px; }   /* 같은 불릿 밑 연속 카드 */
  .msgs-bub-item {
    margin-bottom: 0;
    width: 100%;
    padding: 6px 8px 4px;
    overflow-x: auto;                  /* 표가 박스보다 넓으면 가로 스크롤 */
    -webkit-overflow-scrolling: touch;
  }
  .t-panel {
    padding: 0;
  }
  .t-panel > .d-flex {                 /* 표 카드의 머리 띠 — 차트 카드와 한눈에 갈린다 */
    margin: -6px -8px 6px;
    padding: 5px 8px;
    // background: var(--color-background-secondary);
    background: #83838215;
    border-bottom: 0.5px solid var(--color-border-tertiary);
  }
  .msgs-bub-table {
    border-collapse: collapse;
    width: 100%;
    table-layout: auto;
    font-size: 11px;
    line-height: 1.5;
    background: var(--color-background-primary);
    color: var(--color-text-secondary);
  }
  .msgs-bub-table caption { text-align: left; font-weight: 600; margin-bottom: 6px; font-size: 10.5px; }
  .msgs-bub-table th,
  .msgs-bub-table td {
    border: 0.5px solid var(--color-border-tertiary);
    padding: 5px 8px;
    min-width: 56px;
    vertical-align: top;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }
  .msgs-bub-table th { background: var(--color-background-secondary); color: var(--color-text-primary); font-weight: 600; text-align: left; }
  .msgs-bub-table td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .msgs-bub-table tbody tr:nth-child(even) td { background: rgba(31, 30, 29, 0.03); }

  .msgs-bub-item .apexcharts-canvas .apexcharts-text,
  .msgs-bub-item .apexcharts-canvas .apexcharts-tooltip-text,
  .msgs-bub-item .apexcharts-canvas .apexcharts-datalabel-label,
  .msgs-bub-item .apexcharts-canvas .apexcharts-datalabel {
    font-family: 'AgentArrow', "NanumBarunGothic", Arial, sans-serif !important;
    font-weight: bold;
    filter: none;
  }
  .msgs-bub-item .apexcharts-canvas .apexcharts-legend-series .apexcharts-legend-text {
    color: #6e6b7b !important;
    font-size: 10px !important;
    font-weight: 400 !important;
    padding-left: 0;
  }
  .msgs-bub-item .apexcharts-canvas .apexcharts-point-annotations .apexcharts-point-annotation-label,
  .msgs-bub-item .apexcharts-canvas .apexcharts-xaxis-annotations .apexcharts-xaxis-annotation-label,
  .msgs-bub-item .apexcharts-canvas .apexcharts-yaxis-annotations .apexcharts-yaxis-annotation-label {
    font-size: 9px !important;
  }

  .msgs-bub-item .apexcharts-tooltip-marker {
    width: 8px;
    height: 8px;
    position: relative;
    top: 0px;
    margin-right: 5px;
    border-radius: 50%;
  }

  /* ── 답의 조각 (sections) ─────────────────────────── */
  .example-list { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 2px 4px; }
  .example-btn {
    padding: 4px 10px;
    border-radius: 999px;
    border: 0.5px solid var(--color-border-secondary);
    background: var(--color-background-primary);
    color: var(--color-text-info);
    font-size: 11.5px;
    line-height: 1.4;
    text-align: left;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
  }
  .example-btn:hover    { background: var(--color-background-info); border-color: var(--color-text-info); }
  .example-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .sec-fold {
    margin: 8px 0 2px;
    border: 0.5px dashed var(--color-border-secondary);
    border-radius: var(--border-radius-md);
    background: var(--color-background-primary);
  }
  .sec-fold-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 11.5px;
    color: var(--color-text-tertiary);
    text-align: left;
  }
  .sec-fold-btn:hover { color: var(--color-text-secondary); }
  /* 꺾쇠는 CSS 로 그린다 — ▸ 는 NanumBarunGothic 에 없어 대체 글꼴의 4×4px 점이 됐다 */
  .sec-fold-btn .chev {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin: 0 3px 0 1px;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(-45deg);         /* 닫힘: ▸ */
    transition: transform 0.15s;
  }
  .sec-fold-btn:not(.collapsed) .chev { transform: rotate(45deg); }   /* 열림: ▾ · bootstrap-vue 가 닫힘에 .collapsed 를 단다 */
  .sec-fold .collapse,
  .sec-fold .collapsing { padding: 0 10px 6px; }
  .sec-fold .msgs-bub-line { font-size: 11.5px; color: var(--color-text-tertiary); line-height: 1.7; }

  .sec-notice .msgs-bub-line { margin-top: 6px; font-size: 11px; color: var(--color-text-tertiary); }
  .sec-preamble .msgs-bub-line { margin-top: 6px; font-size: 10px; color: var(--color-text-tertiary); }

  /* ── 질문 패널 ────────────────────────────────────── */
  .q-panel {
    border-top: 0.5px solid var(--color-border-tertiary);
    padding: 12px 14px;
    background: var(--color-background-primary);
  }
  .user-q-panel {
    border-top: 0.5px solid var(--color-border-tertiary);
    padding: 12px 14px;
    background: var(--color-background-secondary);
  }
  .q-label {
    font-size: 11px;
    color: var(--color-text-tertiary);
    font-weight: 500;
    letter-spacing: 0.04em;
  }
  .q-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
  }
  .q-btn {
    height: auto;
    text-align: left;
    padding: 8px 12px;
    border-radius: var(--border-radius-md);
    border: 0.5px solid var(--color-border-secondary);
    background: var(--color-background-primary);
    color: var(--color-text-primary);
    font-size: 13px;
    cursor: pointer;
    transition: background 0.12s;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1.4;
  }
  .q-btn:hover    { background: var(--color-background-info); }
  .q-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .q-btn i        { color: var(--color-text-tertiary); flex-shrink: 0; }

  .user-q-input {
    height: auto;
    text-align: left;
    padding: 8px 12px;
    border-radius: var(--border-radius-md);
    border: 0.5px solid var(--color-border-secondary);
    background: var(--color-background-primary);
    color: var(--color-text-primary);
    font-size: 13px;
    cursor: pointer;
    transition: background 0.12s;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1.4;
  }
  .user-q-input:hover    { background: var(--color-background-primary); }
  .user-q-input:disabled { opacity: 0.4; cursor: not-allowed; }
  /* 범위를 좁히면 이 규칙이 bootstrap 의 .form-control:focus 를 이겨 포커스 표시가 사라진다 — 직접 준다 */
  .user-q-input:focus {
    border-color: var(--color-text-info);
    box-shadow: 0 0 0 2px rgba(50, 102, 173, 0.15);
    outline: none;
  }
  .input-group > .user-q-input {
    border-top-right-radius: var(--border-radius-md) !important;
    border-bottom-right-radius: var(--border-radius-md) !important;
  }

  /* ── 버튼 ─────────────────────────────────────────── */
  .toggle-btn {                       /* 표의 ▲▼ 전용 — 문구가 든 버튼에는 쓰지 않는다 */
    margin-bottom: 2px;
    font-size: 8px;
    color: var(--color-text-primary);
    background: var(--color-background-secondary);
    border: 0.5px solid var(--color-border-tertiary);
    border-radius: var(--border-radius-md);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 10px;
  }
  .reset-btn {
    font-size: 12px;
    color: var(--color-text-secondary);
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0;
  }
  .reset-btn:hover { color: var(--color-text-primary); }

  .copy-container {
    position: relative;               /* 플로팅 버튼의 기준점 */
    display: inline-block;
    width: 100%;
  }
  .copy-btn {
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 10;
    width: 20px;
    height: 20px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 10px;
    transition: background 0.2s;
  }
  .copy-btn:hover { background: rgba(0, 0, 0, 0.85); }

  /* ── 전환 · 접힘 표시 ─────────────────────────────── */
  .fade-enter-active,
  .fade-leave-active { transition: opacity 0.2s ease; }
  .fade-enter-from,
  .fade-leave-to { opacity: 0; }

  .when-open,
  .when-closed { display: none; }
  .collapsed > .when-open,
  :not(.collapsed) > .when-closed { display: none; }
  :not(.collapsed) > .when-open,
  .collapsed > .when-closed { display: inline-block; }
}
</style>